/**
 * Client-Side Logger for Platform Admin
 *
 * Configured with HTTP transport to send logs to auth-gateway-api,
 * where Vector ships them to VictoriaLogs.
 *
 * Note: This file runs in the browser only. Server-side logging uses
 * server-logger.ts which writes structured JSON to stdout.
 */

import {
  createLogger,
  HttpTransport,
  installGlobalErrorHandlers,
  installHydrationErrorInterceptor,
  type LogLevel,
} from '@colix/frontend-logger'
import { runtimeConfig } from './runtime-config'

const API_URL = runtimeConfig('NEXT_PUBLIC_API_URL', 'http://localhost:31315')
const RELEASE =
  process.env.NEXT_PUBLIC_APP_VERSION || process.env.NEXT_PUBLIC_COMMIT_HASH || 'unknown'

const transport =
  typeof window !== 'undefined'
    ? new HttpTransport({
        endpoint: `${API_URL}/api/v1/client-logs`,
        source: 'platform-admin',
        // Platform admin uses session-based auth; no localStorage token to attach.
        // The endpoint accepts unauthenticated requests (rate-limited instead).
      })
    : undefined

/**
 * Singleton logger instance configured for Platform Admin
 */
export const logger = createLogger({
  appVersion: RELEASE,
  release: RELEASE,
  commitHash: process.env.NEXT_PUBLIC_COMMIT_HASH || 'unknown',
  buildTime: process.env.NEXT_PUBLIC_BUILD_TIME || 'unknown',
  environment: process.env.NODE_ENV,
  isDev: process.env.NODE_ENV === 'development',
  minLevel: (process.env.NEXT_PUBLIC_LOG_LEVEL as LogLevel) ?? undefined,
  transport,
})

// Capture uncaught errors and unhandled promise rejections
if (typeof window !== 'undefined') {
  installGlobalErrorHandlers(logger)
  installHydrationErrorInterceptor(logger)
}

// Re-export types for convenience
export type { LogLevel, LogContext } from '@colix/frontend-logger'
